const { prefersReducedMotion } = window.BAO.Utils

class CollectionFeaturedCarousel extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['panel']
  }

  getBreakpoints (slideCount) {
    const count = Number(slideCount)

    return {
      [`(min-width: ${window.BAO.MediaBreakpoints.breakpoints.sm}px)`]: {
        disabled: count <= this.tabletColumns,
        centered: false,
        slides: {
          perView: this.tabletColumns,
          spacing: 20,
        },
      },
      [`(min-width: ${window.BAO.MediaBreakpoints.breakpoints.lg + 1}px)`]: {
        disabled: count <= this.desktopColumns,
        centered: false,
        slides: {
          perView: this.desktopColumns,
          spacing: 20,
        },
      },
    }
  }

  connectedCallback () {
    super.connectedCallback()

    this.fadeDuration =
      parseInt(this.getAttribute('data-fade-duration'), 10) || 250
    this.style.setProperty('--Panel_FadeDuration', `${this.fadeDuration}ms`)

    this.tabletColumns =
      parseInt(this.getAttribute('data-tablet-columns'), 10) || 3
    this.desktopColumns =
      parseInt(this.getAttribute('data-desktop-columns'), 10) || 6

    this.panels = this.els.panel.elements.map(panel =>
      this.initialisePanel(panel),
    )
  }

  initialisePanel (panel) {
    const panelEls = this.getElements(
      ['slides', 'slide', 'next', 'previous', 'listToggle'],
      'data-collection-featured-carousel-el',
      { context: panel },
    )

    const slides = panelEls.slides.element
    const slide = panelEls.slide.element
    const next = panelEls.next.element
    const previous = panelEls.previous.element
    const listToggle = panelEls.listToggle.element

    const carousel = slide
      ? new window.KeenSlider(
        slides,
        {
          selector: panelEls.slide.selector,
          defaultAnimation: {
            duration: 1500,
          },
          loop: true,
          mode: 'snap',
          centered: true,
          disabled: this.dataset.mobileLayout === 'list',
          slides: {
            perView: 1.1,
            spacing: 20,
          },
          breakpoints: this.getBreakpoints(slides.dataset.slideCount),
        },
        [
          window.BAO.Carousel.useInitialised(),
          window.BAO.Carousel.useArrows({ next, previous }),
        ],
      )
      : null

    if (listToggle) {
      listToggle.addEventListener('click', () => {
        this.handleListToggleClick(panel, listToggle)
      })
    }

    this.observeControlsOffset(panel)

    return { group: panel.dataset.group, panel, carousel }
  }

  observeControlsOffset (panel) {
    const media = panel.querySelector(
      '.sec-FeaturedCollectionCard_MediaContainer',
    )

    if (!media) {
      return
    }

    const resizeObserver = new ResizeObserver(([entry]) => {
      panel.style.setProperty(
        '--Carousel_ControlsOffset',
        `${entry.contentRect.height / 2}px`,
      )
    })

    resizeObserver.observe(media)
  }

  setupListeners () {
    super.setupListeners()

    this.handleTabClick = this.handleTabClick.bind(this)
    ;(this.els.tab?.elements || []).forEach(tab => {
      this.listeners.add(tab, 'click', this.handleTabClick)
    })
  }

  handleTabClick (event) {
    const tab = event.currentTarget
    const group = tab.dataset.group

    const nextEntry = this.panels.find(({ group: g }) => g === group)
    if (!nextEntry) {
      return
    }

    const currentEntry = this.panels.find(
      ({ panel }) => panel.getAttribute('aria-hidden') !== 'true',
    )

    if (currentEntry === nextEntry) {
      return
    }

    ;(this.els.tab?.elements || []).forEach(el => {
      el.setAttribute('aria-selected', String(el === tab))
    })

    const revealNext = () => {
      const { panel, carousel } = nextEntry

      if (currentEntry) {
        currentEntry.panel.setAttribute('aria-hidden', 'true')
        currentEntry.panel.style.opacity = ''
      }

      panel.style.opacity = '0'
      panel.setAttribute('aria-hidden', 'false')
      carousel?.update()

      if (prefersReducedMotion()) {
        panel.style.opacity = ''
        return
      }

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          panel.style.opacity = '1'
        })
      })
    }

    if (currentEntry && !prefersReducedMotion()) {
      currentEntry.panel.style.opacity = '0'
      window.setTimeout(revealNext, this.fadeDuration)
    } else {
      revealNext()
    }
  }

  handleListToggleClick (panel, listToggle) {
    const opened = listToggle.dataset.opened === 'true'
    const nextOpened = !opened

    listToggle.dataset.opened = String(nextOpened)
    panel.setAttribute('data-list-open', String(nextOpened))

    listToggle.innerText = nextOpened
      ? listToggle.dataset.seeLessText
      : listToggle.dataset.seeMoreText
  }
}

if (!customElements.get('collection-featured-carousel')) {
  customElements.define(
    'collection-featured-carousel',
    CollectionFeaturedCarousel,
  )
}
